import {
  COLORS,
  drawArc,
  drawArrow,
  drawDashedLine,
  drawPill,
  drawText,
  hexToRgba,
  roundedRect,
} from './shared.js';

function geometry(width, height, params) {
  const theta = (params.angle * Math.PI) / 180;
  const bottom = { x: width * 0.82, y: height * 0.81 };
  const maxByWidth = (bottom.x - width * 0.1) / Math.max(Math.cos(theta), 0.08);
  const maxByHeight = (bottom.y - Math.max(222, height * 0.27)) / Math.max(Math.sin(theta), 0.08);
  const length = Math.max(150, Math.min(maxByWidth, maxByHeight, width * 0.64));
  const top = {
    x: bottom.x - Math.cos(theta) * length,
    y: bottom.y - Math.sin(theta) * length,
  };
  const dir = { x: Math.cos(theta), y: Math.sin(theta) };
  const normal = { x: Math.sin(theta), y: -Math.cos(theta) };
  return { theta, bottom, top, length, dir, normal };
}

export const inclineRenderer = {
  trailPoint() {
    return null;
  },

  draw(ctx, frame) {
    const { width, height, state, params, metrics, options, accent } = frame;
    const geo = geometry(width, height, params);
    const { top, bottom, dir, normal, length: rampPx } = geo;
    const progress = Math.min(1, Math.max(0, state.s / params.length));
    const surfacePoint = {
      x: top.x + dir.x * progress * rampPx,
      y: top.y + dir.y * progress * rampPx,
    };
    const blockSize = Math.min(52, Math.max(34, 32 + params.mass * 4));
    const half = blockSize / 2;
    const center = {
      x: surfacePoint.x + normal.x * (half + 2),
      y: surfacePoint.y + normal.y * (half + 2),
    };

    // 斜面楔形
    ctx.save();
    const wedge = ctx.createLinearGradient(top.x, top.y, bottom.x, bottom.y);
    wedge.addColorStop(0, 'rgba(125, 211, 252, 0.13)');
    wedge.addColorStop(0.55, 'rgba(125, 211, 252, 0.055)');
    wedge.addColorStop(1, 'rgba(125, 211, 252, 0.015)');
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(bottom.x, bottom.y);
    ctx.lineTo(top.x, bottom.y);
    ctx.closePath();
    ctx.fillStyle = wedge;
    ctx.fill();
    ctx.strokeStyle = 'rgba(125, 211, 252, 0.32)';
    ctx.lineWidth = 1.3;
    ctx.stroke();

    // 斜线纹理
    ctx.strokeStyle = 'rgba(125, 211, 252, 0.075)';
    ctx.lineWidth = 1;
    for (let offset = 20; offset < rampPx; offset += 26) {
      const p = { x: top.x + dir.x * offset, y: top.y + dir.y * offset };
      const drop = bottom.y - p.y;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y + 3);
      ctx.lineTo(p.x - Math.min(drop * 0.25, 42), p.y + 3 + Math.min(drop * 0.25, 42) * Math.tan(geo.theta));
      ctx.stroke();
    }
    ctx.restore();

    // 地面
    ctx.save();
    ctx.strokeStyle = 'rgba(156, 195, 224, 0.28)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(width * 0.05, bottom.y + 1);
    ctx.lineTo(width * 0.95, bottom.y + 1);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(156, 195, 224, 0.09)';
    for (let x = width * 0.06; x < width * 0.95; x += 15) {
      ctx.beginPath();
      ctx.moveTo(x, bottom.y + 1);
      ctx.lineTo(x - 9, bottom.y + 10);
      ctx.stroke();
    }
    ctx.restore();

    // 表面高亮与进度
    ctx.save();
    ctx.strokeStyle = hexToRgba(accent, 0.18);
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(bottom.x, bottom.y);
    ctx.stroke();
    if (progress > 0.002) {
      ctx.strokeStyle = hexToRgba(accent, 0.8);
      ctx.shadowColor = hexToRgba(accent, 0.5);
      ctx.shadowBlur = 9;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(surfacePoint.x, surfacePoint.y);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }
    ctx.restore();

    // 刻度
    for (let meter = 0; meter <= params.length + 0.001; meter += params.length / 4) {
      const t = meter / params.length;
      const p = { x: top.x + dir.x * t * rampPx, y: top.y + dir.y * t * rampPx };
      const tick = { x: p.x + normal.x * 7, y: p.y + normal.y * 7 };
      ctx.save();
      ctx.strokeStyle = 'rgba(178, 208, 230, 0.3)';
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(tick.x, tick.y);
      ctx.stroke();
      ctx.restore();
      drawText(ctx, `${meter.toFixed(1)}`, tick.x + normal.x * 8, tick.y + normal.y * 8, {
        color: 'rgba(170, 202, 224, 0.38)',
        font: '500 9px "Cascadia Code", monospace',
        align: 'center',
      });
    }

    // 角度弧
    drawArc(ctx, bottom, 52, Math.PI, Math.PI + geo.theta, {
      color: hexToRgba(accent, 0.6),
      width: 1.5,
    });
    drawText(ctx, `θ ${params.angle}°`, bottom.x - 76, bottom.y - 30, {
      color: hexToRgba(accent, 0.9),
      font: '600 11px "Cascadia Code", monospace',
      align: 'center',
    });

    // 高度虚线
    if (options.grid) {
      drawDashedLine(ctx, top, { x: top.x, y: bottom.y + 1 }, {
        color: 'rgba(148,190,220,.15)',
        dash: [3, 6],
      });
      drawText(ctx, `h ${(params.length * Math.sin(geo.theta)).toFixed(2)} m`, top.x - 10, (top.y + bottom.y) / 2, {
        color: 'rgba(170, 202, 224, 0.4)',
        font: '500 9px "Cascadia Code", monospace',
        align: 'right',
      });
    }

    // 滑块
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(geo.theta);
    ctx.shadowColor = hexToRgba(accent, 0.4);
    ctx.shadowBlur = 16;
    const blockGradient = ctx.createLinearGradient(0, -half, 0, half);
    blockGradient.addColorStop(0, 'rgba(226, 242, 252, 0.96)');
    blockGradient.addColorStop(1, 'rgba(125, 211, 252, 0.55)');
    roundedRect(ctx, -half, -half, blockSize, blockSize, 7);
    ctx.fillStyle = blockGradient;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = hexToRgba(accent, 0.9);
    ctx.lineWidth = 1.2;
    ctx.stroke();
    drawText(ctx, `${params.mass.toFixed(1)}kg`, 0, 1, {
      color: 'rgba(7, 22, 35, 0.82)',
      font: '700 10px "Cascadia Code", monospace',
      align: 'center',
    });
    ctx.restore();

    // 力矢量
    if (options.vectors) {
      const gravityLength = Math.min(52, Math.max(28, params.mass * 9.8 * 0.7));
      drawArrow(
        ctx,
        { x: center.x, y: center.y + half * 0.35 },
        { x: center.x, y: center.y + half * 0.35 + gravityLength },
        { color: COLORS.pink, width: 1.6, head: 6, label: 'mg', labelOffset: { x: 16, y: 12 } },
      );

      const normalLength = Math.min(48, Math.max(18, metrics.extras.normalForce * 1.6));
      drawArrow(
        ctx,
        { x: center.x + normal.x * 4, y: center.y + normal.y * 4 },
        {
          x: center.x + normal.x * (half + normalLength),
          y: center.y + normal.y * (half + normalLength),
        },
        { color: COLORS.cyan, width: 1.4, head: 6, label: 'N', labelOffset: { x: 13, y: -7 } },
      );

      const moving = Math.abs(state.v) > 0.02;
      const frictionDir = moving ? -Math.sign(state.v) : -1;
      const frictionStart = {
        x: center.x - dir.x * half * 0.55,
        y: center.y - dir.y * half * 0.55,
      };
      const frictionLength = Math.min(36, Math.max(9, metrics.extras.frictionMagnitude * 1.6));
      drawArrow(
        ctx,
        frictionStart,
        {
          x: frictionStart.x + dir.x * frictionLength * frictionDir,
          y: frictionStart.y + dir.y * frictionLength * frictionDir,
        },
        {
          color: COLORS.amber,
          width: 1.4,
          head: 6,
          dashed: !moving,
          label: 'f',
          labelOffset: { x: -15, y: 11 },
        },
      );

      // 重力分解（仅显示沿斜面分量，避免与 N 标签重叠）
      const sinLen = Math.min(46, metrics.extras.gravityAlong * 0.62);
      drawArrow(
        ctx,
        { x: center.x, y: center.y + 3 },
        { x: center.x + dir.x * sinLen, y: center.y + dir.y * sinLen },
        {
          color: 'rgba(244,114,182,.48)',
          width: 1.1,
          head: 5,
          dashed: true,
          label: 'mg sinθ',
          labelOffset: { x: 24, y: 14 },
        },
      );
    }

    // 状态
    if (state.status === 'bottom') {
      drawPill(ctx, '已到达底端', bottom.x - 20, top.y + 24, {
        align: 'right',
        color: COLORS.mint,
        bg: 'rgba(6,26,24,.8)',
        border: 'rgba(94,234,212,.3)',
        font: '600 11px "Microsoft YaHei", sans-serif',
      });
    }

    // 底部读数
    const bottomY = height - 30;
    drawPill(ctx, `a ${metrics.extras.acceleration.toFixed(2)} m/s²`, width * 0.5 - 126, bottomY, {
      color: COLORS.text,
      bg: 'rgba(7,18,30,.7)',
      border: 'rgba(148,190,220,.14)',
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `v ${state.v.toFixed(2)} m/s`, width * 0.5 + 2, bottomY, {
      color: accent,
      bg: hexToRgba(accent, 0.07),
      border: hexToRgba(accent, 0.24),
      font: '600 11px "Cascadia Code", monospace',
    });
    drawPill(ctx, `μ ${params.friction.toFixed(2)}`, width * 0.5 + 120, bottomY, {
      color: COLORS.muted,
      bg: 'rgba(7,18,30,.6)',
      border: 'rgba(148,190,220,.12)',
      font: '600 11px "Cascadia Code", monospace',
    });
  },
};
